import Dropdown from '@/Components/Dropdown';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faUser, faFileLines} from '@fortawesome/free-regular-svg-icons';
import {faArrowRightFromBracket, faGear, faSliders, faMoon} from '@fortawesome/free-solid-svg-icons';
import {resetDarkMode, useToggleDarkMode} from '@/hooks/useDarkMode';
import {User} from '@/types';
import {useMemo} from "react";

interface UserProfileDropdownProps {
    user: User;
    variant?: 'text' | 'icon';
}

export default function UserProfileDropdown({user, variant = 'text'}: UserProfileDropdownProps) {
    const name = useMemo(() => user.name.split(' ')[0], [user.name]);
    const {isDark, toggle: toggleDarkMode} = useToggleDarkMode();
    return (
        <Dropdown>
            <Dropdown.Trigger>
                {variant === 'text' ? (
                    <span className="inline-flex rounded-md">
                        <button
                            type="button"
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 dark:text-gray-300 bg-white dark:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-100 focus:outline-none transition ease-in-out duration-150"
                        >
                            <span>{name}</span>
                            <svg
                                className="ms-2 -me-0.5 h-4 w-4"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </button>
                    </span>
                ) : (
                    <button
                        type="button"
                        className="w-9 h-9 rounded-lg font-bold bg-gray-100 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 transition-colors"
                        title={user.name}
                    >
                        {name.charAt(0).toUpperCase()}

                    </button>
                )}
            </Dropdown.Trigger>

            <Dropdown.Content width="lg">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 text-sm">
                    <div className="flex items-center gap-3">

                        <div className="flex flex-col min-w-0">
                            <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{user.name}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{user.email}</div>
                        </div>
                    </div>
                </div>
                <Dropdown.Link className="flex" href={route('profile.edit')}>
                    <FontAwesomeIcon
                        className="text-gray-500 dark:text-gray-400 w-4 h-4 pr-3 self-center"
                        icon={faUser}
                    />
                    <span>Profile</span>
                </Dropdown.Link>
                <Dropdown.Link className="flex" href={route('billing.credits')}>
                    <FontAwesomeIcon
                        className="text-gray-500 dark:text-gray-400 w-4 h-4 pr-3 self-center"
                        icon={faFileLines}
                    />
                    <span>Billing</span>
                </Dropdown.Link>
                <Dropdown.Link className="flex" href={route('setting.edit')}>
                    <FontAwesomeIcon
                        className="text-gray-500 dark:text-gray-400 w-4 h-4 pr-3 self-center"
                        icon={faSliders}
                    />
                    <span>Settings</span>
                </Dropdown.Link>
                {user.is_admin && (
                    <Dropdown.Link isExternal className="flex" href="/admin">
                        <FontAwesomeIcon
                            className="text-gray-500 dark:text-gray-400 w-4 h-4 pr-3 self-center"
                            icon={faGear}
                        />
                        <span>Admin</span>
                    </Dropdown.Link>
                )}
                <div
                    className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition duration-150 ease-in-out"
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleDarkMode();
                    }}
                >
                    <div className="flex items-center text-sm leading-5 text-gray-700 dark:text-gray-300">
                        <FontAwesomeIcon
                            className="text-gray-500 dark:text-gray-400 w-4 h-4 pr-3"
                            icon={faMoon}
                        />
                        <span>Dark Mode</span>
                    </div>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={isDark}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                            isDark ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
                        }`}
                    >
                        <span className="sr-only">Dark Mode</span>
                        <span
                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                isDark ? 'translate-x-4' : 'translate-x-0'
                            }`}
                        />
                    </button>
                </div>
                <Dropdown.Link
                    className="flex"
                    href={route('logout')}
                    method="post"
                    as="button"
                    onSuccess={resetDarkMode}
                >
                    <FontAwesomeIcon
                        className="text-gray-500 dark:text-gray-400 w-4 h-4 pr-3 self-center"
                        icon={faArrowRightFromBracket}
                    />
                    <span>Log Out</span>
                </Dropdown.Link>
            </Dropdown.Content>
        </Dropdown>
    );
}
